import { Injectable } from '@angular/core';
import { ZuperDialogService } from '@zuper/components/zuper-dialog';
import { ProfileSettingsComponent } from './profile-settings.component';
import { LocalStorageService } from 'app/core/services/localstorage.service';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { UserService } from 'app/core/user/user.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';

@Injectable({
  providedIn: 'root'
})
export class ProfileSettingsService {

  public userDetailsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private httpClient: HttpClient, private zuperDialogService: ZuperDialogService, private localStorageService: LocalStorageService,
    private dataCollectorService: DataCollectorService, private userService: UserService, private localisationService: LocalisationService) { }

  /**
   * Open Profile Settings Dialog
   */
  public showProfileSettingsDialog(): void {
    this.zuperDialogService.openCustom(ProfileSettingsComponent, {});
  }

  /**
   * Get User Details from Local Storage
   *
   * @returns User Info Object
   */
  public getUserDataFromLocalStorage(): any {
    const userInfo = {
      userDetails: this.localStorageService.getLocalStorage('user'),
      userPreferences: this.localStorageService.getLocalStorage('user_preferences')
    };
    return userInfo;
  }

  /**
   * Update User Preferences
   *
   * @param userID
   * @param body
   * @returns
   */
  public updateUserPreferences(userID: string, body: any): Observable<any> {
    const url = `${this.dataCollectorService.baseurl()}/user/${userID}/update`;
    return this.httpClient.put(url, body);
  }

  /**
   * Update User details in Local Storage after Profile update
   */
  public updateUserDetailsInLocalStorage(): void {
    forkJoin(
      {
        userPreferences: this.httpClient.get(`${this.dataCollectorService.baseurl()}/user/preferences`),
        userDetails: this.httpClient.get(`${this.dataCollectorService.baseurl()}/user`)
      }
    ).pipe(
      map((response: any) => {
        if (response && response !== null) {
          this.userService._user.next(response.userDetails.data);
          this.localStorageService.setLocalStorage('user', response.userDetails.data);
          this.localStorageService.setLocalStorage('user_preferences', response.userPreferences.data);
          const selectedLanguage = response.userPreferences?.data?.language;
          this.localisationService.loadSelectedLanguage(selectedLanguage);
        }
      })
    ).subscribe({
      next: () => {
        this.userDetailsSubject.next(true);
      }
    });
  }

  /**
   * Save changed user password
   *
   * @param body Password Details
   * @returns Observable<any>
   */
  public saveChangeUserPassword(body: any): Observable<any> {
    const url = `${this.dataCollectorService.baseurl()}/user/update_password`;
    return this.dataCollectorService.postData(url, body);
  }

  /**
   * Get Multi Factor Auth details of the user
   */
  public getMultiFactorAuthDetails(): Observable<any> {
    const url = `${this.dataCollectorService.baseurl()}/user/2fa`;
    return this.dataCollectorService.getData(url);
  }

  /**
   * Get Multi Factor Auth Setup Details
   */
  public getAuthenticatorAppSetupConfig(body: any): Observable<any> {
    const url = `${this.dataCollectorService.baseurl()}/user/2fa/setup`;
    return this.dataCollectorService.postData(url, body);
  }

  /**
   * Verify Authenticator App OTP
   */
  public verifyAuthenticatorApp(body: any): Observable<any> {
    const url = `${this.dataCollectorService.baseurl()}/user/2fa/verify`;
    return this.dataCollectorService.postData(url, body);
  }

  /**
   * Get Recovery Codes
   */
  public getRecoveryCodes(): Observable<any> {
    const url = `${this.dataCollectorService.baseUrl}/user/2fa/recovery_codes`;
    return this.dataCollectorService.getData(url);
  }

  /**
   * Generate New Recovery Codes
   */
  public generateRecoveryCodes(): Observable<any> {
    const url = `${this.dataCollectorService.baseurl()}/user/2fa/recovery_codes`;
    return this.dataCollectorService.postData(url, {});
  }

  /**
   * Retry OTP Verification
   */
  public retryOtpVerification(body: any): Observable<any> {
    const url = `${this.dataCollectorService.baseurl()}/user/2fa/retry_otp`;
    return this.dataCollectorService.postData(url, body);
  }

  /**
   * Remove 2FA setup
   */
  public remove2FaSetup(mode: string): Observable<any>{
    const url = `${this.dataCollectorService.baseUrl}/user/2fa?mode=${mode}`;
    return this.dataCollectorService.deleteData(url);
  }

}
